import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, CircularProgress, FormControl, Select, MenuItem, TextField } from '@mui/material';
import { AddButton } from '../button/button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { updateCompletedTask } from '../../redux/slices/upload';
import { STATUS } from '../../utils/status';
import { useParams } from 'react-router-dom';
import { setSingleSelectEdited } from '../../redux/reducers/isEditTaskCompleted';
import { getCookie } from '../login/cookies';
import { constants } from '../../utils/constants';

const OTHER_VALUE = '___other___';

interface SingleSelectActionProps {
    actionValue: any[];
    actionIndex: number;
    result: any;
    action: string;
    projectStatus: string;
    heading?: string;
}

export default function SingleSelectAction({ action, actionValue, actionIndex, result, projectStatus, heading }: SingleSelectActionProps) {
    const isProjectStatusCompleted = projectStatus && projectStatus === 'COMPLETED';
    const [selectedValues, setSelectedValues] = useState<any[]>(actionValue);
    const dispatch = useDispatch<AppDispatch>();
    const { updateCompletedTaskStatus } = useSelector((state: RootState) => state.upload);
    const fetchPermissions = useSelector((state: RootState) => state.projects.permissions)
    const [currentConfig, setcurrentConfig] = useState<any>(undefined);
    const { id } = useParams();
    const [isEdited, setIsEdited] = useState(false);
    const [configOptions, setConfigOptions] = useState<any>({});
    const [loadingOptions, setLoadingOptions] = useState<any>({});
    console.log({ isEdited })

    // Render message inside JSX below when there are no options.
    const fetchingRef = useRef<Record<string, boolean>>({});

    const handleDropdownOpen = useCallback(async (configId: string) => {
        if (configOptions[configId]) {
            console.log('Options already cached for config:', configId, configOptions[configId]);
            return;
        }

        // Avoid starting another fetch if one is already in progress for this configId.
        if (fetchingRef.current[configId]) {
            console.log('Fetch already in progress for config:', configId);
            return;
        }

        fetchingRef.current[configId] = true;
        setLoadingOptions((prev: any) => ({ ...prev, [configId]: true }));
        try {
            const response = await fetch(`${constants.API_BASE_URL}project/common/config_options/?config_id=${configId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getCookie('token')}`
                }
            });

            const result = await response.json();

            if (result && result.data && Array.isArray(result.data)) {
                console.log('Setting options for config:', configId, result.data);
                setConfigOptions((prev: any) => ({ ...prev, [configId]: result.data }));
            } else if (result && Array.isArray(result)) {
                console.log('Setting options from direct array:', configId, result);
                setConfigOptions((prev: any) => ({ ...prev, [configId]: result }));
            } else if (result && result.results) {
                console.log('Setting options from results field:', configId, result.results);
                setConfigOptions((prev: any) => ({ ...prev, [configId]: result.results }));
            }
        } catch (error) {
            console.error('Error fetching config options:', error);
        } finally {
            fetchingRef.current[configId] = false;
            setLoadingOptions((prev: any) => ({ ...prev, [configId]: false }));
        }
    }, [configOptions]);

    // Prefetch options when task details (and single select configs) are loaded.
    useEffect(() => {
        if (!actionValue || !Array.isArray(actionValue) || actionValue.length === 0) return;
        actionValue.forEach((val: any) => {
            const configId = val?.id;
            if (configId) {
                handleDropdownOpen(configId);
            }
        });
    }, [actionValue, handleDropdownOpen]);

    const getOptionsForConfig = (configId: string, fallbackOptions: any[]) => {
        const fetchedOptions = configOptions[configId];
        console.log('Getting options for config:', configId, 'Fetched:', fetchedOptions, 'Fallback:', fallbackOptions);
        if (fetchedOptions) {
            const transformed = fetchedOptions.map((opt: any, idx: number) => ({
                index: idx,
                value: opt.value,
                id: opt.id,
                output_value: opt.output_value,
                is_other: opt.is_other
            }));
            console.log('Transformed options:', transformed);
            return transformed;
        }
        return fallbackOptions;
    };

    const handleChange = (valueIndex: number, selectedValue: string, optionIndex: number, isOther = false) => {
        if (isProjectStatusCompleted) return;

        const updatedValues = [...selectedValues];
        if (isOther) {
            updatedValues[valueIndex] = {
                ...updatedValues[valueIndex],
                selected: [{
                    ...updatedValues[valueIndex].selected?.[0],
                    index: optionIndex,
                    value: updatedValues[valueIndex].selected?.[0]?.is_other
                        ? updatedValues[valueIndex].selected[0].value
                        : '',
                    is_other: true,
                    output_value: updatedValues[valueIndex].selected?.[0]?.is_other
                        ? updatedValues[valueIndex].selected[0].output_value
                        : ''
                }]
            };
        } else {
            updatedValues[valueIndex] = {
                ...updatedValues[valueIndex],
                selected: [{ index: optionIndex, value: selectedValue, is_other: false, output_value: selectedValue }]
            };
        }
        setSelectedValues(updatedValues);
        setIsEdited(true);
        dispatch(setSingleSelectEdited(true));
    };

    const handleOtherTextChange = (valueIndex: number, text: string) => {
        if (isProjectStatusCompleted) return;

        const updatedValues = [...selectedValues];
        updatedValues[valueIndex] = {
            ...updatedValues[valueIndex],
            selected: [{
                ...updatedValues[valueIndex].selected?.[0],
                value: text,
                is_other: true,
                output_value: text
            }]
        };
        setSelectedValues(updatedValues);
        setIsEdited(true);
        dispatch(setSingleSelectEdited(true));
    };

    const handleSave = () => {
        if (isProjectStatusCompleted) return;

        setcurrentConfig(actionIndex);
        const payload = {
            project_id: id,
            task_id: result.tasks[0].task_id,
            allocation_id: result.tasks[0].task_allocation_id,
            response_id: result.tasks[0].responses[0].id,
            action_id: actionIndex,
            action: selectedValues[0].config_details.name,
            action_value: {
                id: selectedValues[0].id,
                selected: selectedValues[0].selected
            }
        };
        dispatch(updateCompletedTask(payload));
        setIsEdited(false);
        dispatch(setSingleSelectEdited(false));
    };

    const renderedSelects = selectedValues.length === 0 ? (
        <Typography>No radio options available.</Typography>
    ) : (
        selectedValues.map((value: any, valueIndex: number) => {
            const configId = value.id;
            const options = getOptionsForConfig(configId, value.config_details?.value?.value || []);
            return (
                <div
                    key={valueIndex}
                    style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}
                >
                    <Typography sx={{ fontWeight: 'bold', marginRight: '20px' }}>
                        {value.config_details?.value?.label}:
                    </Typography>
                    {value.selected?.length === 0 || value.selected?.[0]?.length === 0 ? (
                        <Typography sx={{ fontStyle: 'italic', color: 'gray' }}>
                            No selection made.
                        </Typography>
                    ) : (
                        fetchPermissions?.completing_task_action_details?.[1] === "read" ? (
                            <FormControl fullWidth>
                                <Select
                                    value={value.selected?.[0]?.is_other ? OTHER_VALUE : (value.selected?.[0]?.value || '')}
                                    onOpen={() => handleDropdownOpen(configId)}
                                >
                                    {loadingOptions[configId] ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} />
                                        </MenuItem>
                                    ) : (
                                        options.map((option: any, idx: number) => (
                                            <MenuItem key={option.id || option.index || idx} value={option.value}>
                                                {option.value}
                                            </MenuItem>
                                        ))
                                    )}
                                    <MenuItem value={OTHER_VALUE}>Other</MenuItem>
                                </Select>
                                {value.selected?.[0]?.is_other && (
                                    <TextField
                                        size="small"
                                        value={value.selected?.[0]?.value || ''}
                                        placeholder="Other value"
                                        disabled
                                        sx={{ mt: 1 }}
                                    />
                                )}
                            </FormControl>
                        ) : (
                            <FormControl fullWidth>
                                <Select
                                    value={value.selected?.[0]?.is_other ? OTHER_VALUE : (value.selected?.[0]?.value || '')}
                                    onOpen={() => handleDropdownOpen(configId)}
                                    onChange={(e) => {
                                        const selectedValue = e.target.value;
                                        const currentOptions = getOptionsForConfig(configId, value.config_details?.value?.value || []);
                                        if (selectedValue === OTHER_VALUE) {
                                            handleChange(valueIndex, OTHER_VALUE, -1, true);
                                        } else {
                                            const optionIndex = currentOptions.findIndex(
                                                (option: any) => option.value === selectedValue
                                            );
                                            handleChange(valueIndex, selectedValue, optionIndex, false);
                                        }
                                    }}
                                >
                                    {loadingOptions[configId] ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} />
                                        </MenuItem>
                                    ) : (
                                        options.map((option: any, idx: number) => (
                                            <MenuItem key={option.id || option.index || idx} value={option.value}>
                                                {option.value}
                                            </MenuItem>
                                        ))
                                    )}
                                    <MenuItem value={OTHER_VALUE}>Other</MenuItem>
                                </Select>
                                {value.selected?.[0]?.is_other && (
                                    <TextField
                                        size="small"
                                        value={value.selected?.[0]?.value || ''}
                                        placeholder="Enter other value"
                                        onChange={(e) => handleOtherTextChange(valueIndex, e.target.value)}
                                        sx={{ mt: 1 }}
                                    />
                                )}
                            </FormControl>
                        )

                    )}
                </div>
            );
        })
    );

    return (
        <>
            {renderedSelects}

            {!isProjectStatusCompleted && isEdited && (
                fetchPermissions.completing_task_action_details?.[1] === "read" ? (
                    <></>
                ) : (
                    <AddButton
                        startIcon={actionIndex === currentConfig && updateCompletedTaskStatus === STATUS.PENDING ? <CircularProgress /> : null}
                        disabled={actionIndex === currentConfig && updateCompletedTaskStatus === STATUS.PENDING}
                        onClick={() => handleSave()}
                    >
                        Save
                    </AddButton>
                )
            )}
        </>
    );
}
